/**
 * IFS Inforsure FedEx API Service
 * Thin wrapper around apiClient for typed IFS proxy calls
 * Includes smart caching: static data (30min), address lists (5min), lookups (2min)
 */
import { apiClient } from '../utils/apiClient';
import { getActiveIFSAccountId } from '../utils/ifsAccount';
const cache = new Map();
const CACHE_TTL = {
    STATIC: 24 * 60 * 60 * 1000, // 24h — countries, basic data, packaging types
    MEDIUM: 30 * 60 * 1000, // 30 min — address lists, recipient lists
    SHORT: 2 * 60 * 1000, // 2 min — zip lookups, service checks (memory only)
    NONE: 0, // never — cost calc, shipment tracking, rate requests
};
const STORAGE_PREFIX = 'ifs.cache.';
// Only persist the slow-changing tiers; short-lived lookups stay in memory
const PERSIST_MIN_TTL = CACHE_TTL.MEDIUM;
function getCacheKey(endpoint, params, accountId) {
    const sortedParams = Object.keys(params).sort().map(k => `${k}=${params[k]}`).join('&');
    return `${accountId || 'default'}|${endpoint}|${sortedParams}`;
}
function readPersisted(key, ttl) {
    try {
        const raw = localStorage.getItem(STORAGE_PREFIX + key);
        if (!raw)
            return null;
        const entry = JSON.parse(raw);
        if (Date.now() - entry.t >= ttl) {
            localStorage.removeItem(STORAGE_PREFIX + key);
            return null;
        }
        return entry.d;
    }
    catch {
        return null;
    }
}
function writePersisted(key, data) {
    const entry = JSON.stringify({ t: Date.now(), d: data });
    try {
        localStorage.setItem(STORAGE_PREFIX + key, entry);
    }
    catch {
        // Quota exceeded — drop all IFS cache entries and retry once
        try {
            clearPersistedIFSCache();
            localStorage.setItem(STORAGE_PREFIX + key, entry);
        }
        catch { /* give up silently — cache is best-effort */ }
    }
}
function clearPersistedIFSCache() {
    try {
        Object.keys(localStorage)
            .filter(k => k.startsWith(STORAGE_PREFIX))
            .forEach(k => localStorage.removeItem(k));
    }
    catch { /* ignore */ }
}
async function ifsCall(endpoint, params = {}, ttl = 0, opts = {}) {
    const accountId = getActiveIFSAccountId();
    const key = getCacheKey(endpoint, params, accountId);
    if (ttl > 0 && !opts.force) {
        const cached = cache.get(key);
        if (cached && (Date.now() - cached.timestamp) < ttl) {
            return cached.data;
        }
        if (ttl >= PERSIST_MIN_TTL) {
            const persisted = readPersisted(key, ttl);
            if (persisted) {
                const response = { ok: true, status: 200, data: persisted };
                cache.set(key, { data: response, timestamp: Date.now() });
                return response;
            }
        }
    }
    const body = { endpoint, params };
    if (accountId)
        body.accountId = accountId;
    const response = await apiClient.post('/ifs-proxy', body);
    if (ttl > 0 && response.ok && response.data?.success) {
        cache.set(key, { data: response, timestamp: Date.now() });
        if (ttl >= PERSIST_MIN_TTL)
            writePersisted(key, response.data);
    }
    return response;
}
/** Clear all cached IFS data (memory + persisted) */
export function clearIFSCache() {
    cache.clear();
    clearPersistedIFSCache();
}
export const ifsService = {
    // ─── Reference Data ────────────────────────────────────
    /** API #1: Country & State data (cached 30min — rarely changes) */
    getCountryState: () => ifsCall('ca_country_state', {}, CACHE_TTL.STATIC),
    /** API #2: Basic data (cached 30min — packaging, service types, etc.) */
    getBasicData: () => ifsCall('ca_basic_data', {}, CACHE_TTL.STATIC),
    // ─── Address Book ──────────────────────────────────────
    /** API #3: Sender address list (cached 30min + persisted; force to bypass) */
    getClientAddressList: (force = false) => ifsCall('ca_client_address_list', {}, CACHE_TTL.MEDIUM, { force }),
    /** API #4: Sender address detail (cached 5min) */
    getClientAddressData: (clientAddressId) => ifsCall('ca_client_address_data', { client_address_id: clientAddressId }, CACHE_TTL.MEDIUM),
    /** API #5: Recipient list (cached 2min — search results) */
    getRecipientList: (term = '') => ifsCall('ca_recipient_list', { term }, CACHE_TTL.SHORT),
    /** API #6: Recipient detail (cached 5min) */
    getRecipientData: (recipientId) => ifsCall('ca_recipient_data', { recipient_id: recipientId }, CACHE_TTL.MEDIUM),
    /** API #7: Recipient search (cached 2min) */
    searchRecipients: (term) => ifsCall('ca_recipient_search_list', { term }, CACHE_TTL.SHORT),
    // ─── Shipping Tools ────────────────────────────────────
    /** API #8: Zip/service restriction check (cached 2min) */
    checkZipServiceRestriction: (params) => ifsCall('ca_change_zipcode_service', params, CACHE_TTL.SHORT),
    /** API #9/#10: Verify recipient address */
    verifyRecipientAddress: (params) => ifsCall('ca_verify_recipient_address', params),
    /** API #12: Zip code lookup (cached 30min — zip data doesn't change) */
    getZipDetails: (zip) => ifsCall('ca_get_zipcode_related_details', { zip }, CACHE_TTL.STATIC),
    /** API #13: Zone ID lookup */
    getZoneId: (params) => ifsCall('ca_get_zone_id', params),
    /** API #14: Service/packaging compatibility (cached 30min — static rules) */
    checkServicePackageRestriction: (packagingType) => ifsCall('ca_restrict_service_type_from_package_type', { packaging_type: packagingType }, CACHE_TTL.STATIC),
    /** API #15: Package dimensions notification */
    checkPackageDimensions: (params) => ifsCall('ca_notification_for_package_weight', {
        provider: 'fedex',
        ...params,
    }),
    /** API #16: Package weight check */
    checkPackageWeight: (params) => ifsCall('ca_check_package_weight', params),
    /** API #17: Declare value check */
    checkDeclareValue: (params) => ifsCall('ca_check_declare_value', params),
    /** API #18: Payment type info (cached 30min — static rules) */
    checkPaymentType: (paymentType) => ifsCall('ca_change_payment_type', { payment_type: paymentType }, CACHE_TTL.STATIC),
    /** API #19: HAL locations */
    getHALLocations: (params) => ifsCall('ca_get_hold_for_pickup_location', params),
    /** API #25: Pickup schedule check */
    checkPickup: (params) => ifsCall('ca_check_pick_up', params),
    // ─── Cost Calculator ───────────────────────────────────
    /** API #20: Full cost calculation */
    calculateCost: (params) => ifsCall('ca_calculate_cost', params),
    // ─── Shipment Tracking ─────────────────────────────────
    /** API #27.1 (v2.3): Shipping label as base64 PDF — replaces removed ca_shipment_view_options */
    getLabelPDF: (params) => ifsCall('ca_display_shipment', params),
    /** API #27.2 (v2.3): 4x6 thermal label as base64 PDF */
    getThermalLabelPDF: (params) => ifsCall('ca_front_ship_history', { mode: 'ViewLabelPDF', ...params }),
    /** API #28: Full shipment details */
    getShipmentDetails: (params) => ifsCall('ca_view_shipment_details', params),
    /** API #29: AES ITN status */
    checkAESITN: (shipmentId) => ifsCall('ca_check_aes_itn_no', { shipment_id: shipmentId }),
    // ─── International / AES ───────────────────────────────
    /** API #21: Loading port lookup */
    getLoadingPort: (params) => ifsCall('ca_get_loading_port_data', params),
    /** API #22: Product descriptions */
    getProductDescriptions: (productName) => ifsCall('ca_get_products_description', { product_name: productName }),
    /** API #23: HTS numbers */
    getHTSNumbers: (params) => ifsCall('ca_get_products_htsno_unit', params),
    /** API #24: Rate request */
    getRateRequest: (params) => ifsCall('ca_get_rate_request', params),
};
export default ifsService;
